import type { WorkerStanding } from '../index.js'
import { TIER_HEX, TIER_LABELS } from '../../core/index.js'
import { employmentTypeNote } from '../../core/index.js'
import { ScoreStanding, TIER_RANK, onTrackExplanation } from './ScoreStanding.js'

function daysUntil(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000))
}

const YEAR_MS = 365.25 * 86_400_000

/** "2.5 yrs", "8 mos" or "new" from the first day; null when it is not recorded or lies ahead. */
export function describeService(hireDate: string | null, now: number = Date.now()): string | null {
  if (!hireDate) {
    return null
  }
  const since = Date.parse(`${hireDate}T00:00:00`)
  if (Number.isNaN(since) || since > now) {
    return null
  }
  const years = (now - since) / YEAR_MS
  if (years < 1 / 12) {
    return 'new'
  }
  if (years < 1) {
    const months = Math.max(1, Math.floor(years * 12))
    return `${months} ${months === 1 ? 'mo' : 'mos'}`
  }
  const rounded = Math.round(years * 2) / 2
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)} yrs`
}

function NextTierPanel({ standing }: { standing: WorkerStanding }) {
  if (standing.isTopTier) {
    return (
      <div className="bg-gold-lt border border-gold/50 p-3 text-center">
        <div className="text-xs font-extrabold text-ink">You hold the top tier.</div>
      </div>
    )
  }
  // The running score already points above the tier held: the card above says so (on track, and until when), so there is no next tier to add.
  if (standing.onTrack && TIER_RANK[standing.onTrack.tier] > TIER_RANK[standing.tier]) {
    return null
  }
  const next = standing.nextTier
  if (!next) {
    // The facility has not configured tier thresholds, so there is nothing truthful to say about the next tier.
    return null
  }

  const tierName = TIER_LABELS[next.tier]
  return (
    <div className="bg-white border border-marine/15 p-3">
      <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1">Next tier</div>
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-sm font-extrabold" style={{ color: TIER_HEX[next.tier] }}>
          {tierName}
        </div>
        <div className="text-xs font-bold text-ink">
          {next.pointsNeeded > 0 ? (
            <>
              {next.pointsNeeded} {next.pointsNeeded === 1 ? 'point' : 'points'} to go
            </>
          ) : (
            <>Score reached, tier held</>
          )}
        </div>
      </div>
      <div className="text-[11px] text-ink-mid mt-1">
        {tierName} starts at a score of {next.minScore}.
        {/* Past the line yet not moving: the close rule that holds the tier says why. */}
        {next.pointsNeeded <= 0 && standing.onTrack && onTrackExplanation(standing.tier, standing.onTrack) && <> {onTrackExplanation(standing.tier, standing.onTrack)}</>}
        {next.bidWindowOpensAt && (
          <>
            {' '}
            {tierName} bid window opens: <span className="font-bold text-ink">{next.bidWindowOpensAt}</span>.
          </>
        )}
      </div>
    </div>
  )
}

/**
 * A worker's standing as one card: tier, score, the tier the score is on
 * track for when that differs, the next tier and how far away it is, and the one thing most worth working on. Rendered entirely
 * from `GET /workers/{workerId}/standing`, with no other call and no
 * arithmetic of its own - which makes it the reference for how a partner
 * can show standing inside its own app.
 */
export function StandingCard({ standing }: { standing: WorkerStanding }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white border border-marine/15 p-5 text-center">
        <div className="text-[11px] font-bold text-ink-mid tracking-wide mb-2">
          {standing.name.toUpperCase()} · {standing.role}
          {employmentTypeNote(standing) && <span className="font-semibold"> · {employmentTypeNote(standing)}</span>}
          {describeService(standing.hireDate) && <span className="font-semibold"> · {describeService(standing.hireDate)}</span>}
        </div>
        <ScoreStanding score={standing.score} tier={standing.tier} onTrack={standing.onTrack} tierSuffix=" Tier" />
        <div className="text-[11px] text-ink-mid mt-2">Resets in {daysUntil(standing.scoreResetsAt)} days</div>
      </div>

      <NextTierPanel standing={standing} />

      {standing.coaching && (
        <div className="bg-coral-lt border border-coral/40 p-3">
          <div className="text-[9px] font-bold uppercase tracking-wide text-coral mb-1">Most worth working on</div>
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-xs font-extrabold text-ink">{standing.coaching.label}</div>
            <div className="text-xs font-bold text-ink">{standing.coaching.value}</div>
          </div>
          <div className="text-[11px] text-ink-mid mt-1 leading-snug">{standing.coaching.nudge}</div>
        </div>
      )}
    </div>
  )
}
